import { useEffect, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { Router } from 'next/router';

import { INewslatterData, INewsletterFormData } from './interfaces';
import { Result } from '../../core/Footer/interfaces';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { generateNewsletterFormSchema } from '../../../utils/generateNewsletterFormSchema';

import { submit_hubspot_newsletter } from '../../../utils/hubspot/hubspot_newsletter';

export const useNewsletter = (dataNewsletter: INewslatterData) => {
  const [isAgreementShown, setIsAgreementShown] = useState<boolean>(false);
  const [modalIsOpen, setIsModalOpen] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [result, setResult] = useState<Result>(Result.None);

  const { width } = useWindowSize();
  const newsletterRef = useRef<any>(null);
  const agreementRef = useRef<any>(null);
  const checkboxRef = useRef<any>(null);
  const agreementContainerRef = useRef<any>(null);

  const handleShowAgreement = (e: any) => {
    e.preventDefault();
    setIsAgreementShown(true);
    if (width && width < 769) {
      window.scrollTo({
        top: newsletterRef?.current?.offsetTop + 60,
        behavior: 'smooth',
      });
    }
  };

  const handleCloseAgreement = (e: any) => {
    e.preventDefault();
    setIsAgreementShown(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: any) => {
      if (
        agreementContainerRef?.current &&
        !agreementContainerRef?.current?.contains(e.target) &&
        !checkboxRef?.current?.contains(e.target)
      ) {
        setIsAgreementShown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [agreementContainerRef]);

  const schema = generateNewsletterFormSchema({
    email: dataNewsletter.errors,
    firstname: dataNewsletter.errors_firstname,
    agreement: dataNewsletter.errors_agreement,
  });

  const formMethods = useForm({
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      firstname: '',
      email: '',
      agreement: false,
    },
  });

  const onRouteChangeComplete = () => {
    formMethods.reset();
    setSubmitting(false);
    setSubmitted(false);
    setResult(Result.None);
  };

  useEffect(() => {
    Router.events.on('routeChangeComplete', onRouteChangeComplete);

    return () => {
      Router.events.off('routeChangeComplete', onRouteChangeComplete);
    };
  }, []);

  const handleTryAgain = () => {
    formMethods.reset();
    setSubmitting(false);
    setSubmitted(false);
  };

  const handleSubmit = async (data: INewsletterFormData) => {
    setSubmitting(true);
    setResult(Result.None);

    try {
      const hubspot_response = await submit_hubspot_newsletter(data);

      setSubmitting(false);
      setSubmitted(true);
      setResult(Result.Success);
      setIsModalOpen(true);

      setTimeout(() => {
        formMethods.reset();
        setSubmitted(false);
      }, 500);
    } catch (e: unknown) {
      setSubmitting(false);
      setSubmitted(true);
      setResult(Result.Error);

      setTimeout(() => {
        formMethods.reset();
        setSubmitted(false);
      }, 500);
    }
  };

  return {
    handleSubmit,
    formMethods,
    handleCloseAgreement,
    handleShowAgreement,
    newsletterRef,
    checkboxRef,
    submitted,
    submitting,
    width,
    isAgreementShown,
    modalIsOpen,
    setIsModalOpen,
    agreementRef,
    agreementHeight: agreementRef.current?.clientHeight,
    agreementContainerRef,
  };
};
